import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useAgenticConversation, useChatbotConversation, useEndAgenticSession, useEndOldChatbotSession } from '@components/Copilot/hooks';
import {
  AgentId,
  ChatMessage,
  GenerateChatbotSummaryData,
  GenerateChatbotSummaryRequest,
  PublishChatbotMessageData,
  PublishChatbotMessageRequest,
  TAgentState,
  TCopilotError,
  TSessionProps,
  UserPrompt,
} from '@components/Copilot/types';
import { FeatureFlags } from '@fabric-ui/components';
import { asyncNoop, noop, QueryKey } from '@fabric-ui/utils';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { useOnSubNavigate } from '@utils/AvRouter';
import { useAuthContext } from './AuthContextProvider';

const defaultLastSessionProps: TSessionProps = {
  agentId: AgentId.TICKET_AGENT,
  additionalParams: {
    entityKey: '',
    projectionId: { name: '', builtIn: true },
  },
  actions: [],
  getConfirmDialogConfig: undefined,
};

export interface IAvCopilotContext {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sessionId: string;
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  threadId: string;
  setThreadId: React.Dispatch<React.SetStateAction<string>>;
  restartCopilotSession: () => Promise<void>;
  endCopilotSession: () => void;
  forceDiscardCopilot: (keepSession?: boolean) => void;
  closeCopilotWithBlocker: () => void;
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  postMessage: (message: UserPrompt) => void;
  publishPrompt: UseMutateFunction<PublishChatbotMessageData, Error, PublishChatbotMessageRequest, unknown>;
  summarizeMessage: (variables: GenerateChatbotSummaryRequest, options?: { onSuccess: (data: GenerateChatbotSummaryData) => void }) => void;
  isPollingEnabled: boolean;
  isSummarizing: boolean;
  conversationErrorMessage: TCopilotError | null;
  setConversationErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
  onOpenCopilot: (props: TSessionProps) => void;
  lastSessionProps: TSessionProps;
  isDirty: boolean;
  isNavigationBlockerOpen: boolean;
  setIsNavigationBlockerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDefaultPromptsOpen: boolean;
  setIsDefaultPromptsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showActionButtons: boolean;
  setShowActionButtons: React.Dispatch<React.SetStateAction<boolean>>;
  excludeStateUpdate: boolean;
  setExcludeStateUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  stateRef: React.RefObject<{ currentState: TAgentState; lastKnownState: TAgentState }>;
  thinkingText: string;
  isSSEEnabled: boolean;
}

export const AvCopilotContext = React.createContext<IAvCopilotContext>({
  isOpen: false,
  setIsOpen: noop,
  sessionId: '',
  setSessionId: noop,
  threadId: '',
  setThreadId: noop,
  restartCopilotSession: asyncNoop,
  endCopilotSession: noop,
  forceDiscardCopilot: noop,
  closeCopilotWithBlocker: noop,
  messages: [],
  addMessage: noop,
  postMessage: noop,
  publishPrompt: noop,
  isPollingEnabled: false,
  summarizeMessage: noop,
  isSummarizing: false,
  conversationErrorMessage: null,
  setConversationErrorMessage: noop,
  onOpenCopilot: noop,
  lastSessionProps: defaultLastSessionProps,
  isDirty: false,
  isNavigationBlockerOpen: false,
  setIsNavigationBlockerOpen: noop,
  isDefaultPromptsOpen: false,
  setIsDefaultPromptsOpen: noop,
  showActionButtons: true,
  setShowActionButtons: noop,
  excludeStateUpdate: true,
  setExcludeStateUpdate: noop,
  stateRef: { current: { currentState: undefined, lastKnownState: undefined } },
  thinkingText: 'Thinking',
  isSSEEnabled: false,
});

export function AvCopilotContextProvider({ children }: React.PropsWithChildren = {}) {
  const queryClient = useQueryClient();
  const { featureFlags } = useAuthContext();
  const isSSEEnabled = !!featureFlags[FeatureFlags.AgentSSE];
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isNavigationBlockerOpen, setIsNavigationBlockerOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationErrorMessage, setConversationErrorMessage] = useState<TCopilotError | null>(null);
  const [lastSessionProps, setLastSessionProps] = useState<TSessionProps>(defaultLastSessionProps);
  const [isDefaultPromptsOpen, setIsDefaultPromptsOpen] = useState<boolean>(false);
  const [showActionButtons, setShowActionButtons] = useState<boolean>(true);
  const [excludeStateUpdate, setExcludeStateUpdate] = useState<boolean>(true);
  const stateRef = useRef<{ currentState: TAgentState; lastKnownState: TAgentState }>({
    currentState: undefined,
    lastKnownState: undefined,
  });
  const isOldImplementation = lastSessionProps.agentId === AgentId.TICKET_AGENT;

  const isDirty = useMemo(
    () => messages.length > 1 && !!lastSessionProps.actions.length && !!lastSessionProps.getConfirmDialogConfig,
    [lastSessionProps.actions.length, lastSessionProps.getConfirmDialogConfig, messages.length]
  );
  const chatRunInfo = useMemo(() => ({ threadId }), [threadId]);

  const { mutate: endSession } = useEndAgenticSession();
  const { mutate: endOldSession } = useEndOldChatbotSession();

  const resetCopilotState = useCallback(() => {
    setSessionId('');
    setThreadId('');
    setMessages([]);
    setConversationErrorMessage(null);
    setExcludeStateUpdate(true);
    stateRef.current = { currentState: undefined, lastKnownState: undefined };
  }, []);

  const endCopilotSession = useCallback(() => {
    if (lastSessionProps.retainSessionOnClose) {
      return;
    }
    if (isOldImplementation) {
      if (chatRunInfo.threadId) {
        resetCopilotState();
        endOldSession({ chatRunInfo });
      }
    } else if (sessionId) {
      resetCopilotState();
      endSession({ sessionId });
    }
  }, [sessionId, threadId, isOldImplementation, resetCopilotState, endSession, endOldSession, lastSessionProps.retainSessionOnClose]);

  const onOpenCopilot = (sessionProps: TSessionProps) => {
    const isOldImplementation = sessionProps.agentId === AgentId.TICKET_AGENT;
    if (!isOldImplementation || (sessionProps?.additionalParams?.entityKey && sessionProps?.additionalParams?.projectionId)) {
      setIsOpen(true);
      setLastSessionProps(sessionProps);
      if (sessionProps.retainSessionOnClose) {
        initBasePathToRetain();
      }
    }

    if (isOldImplementation) {
      if (chatRunInfo.threadId) {
        endCopilotSession();
      } else if (lastSessionProps) {
        resetCopilotState();
      }
    } else if (sessionId) {
      endCopilotSession();
    } else if (lastSessionProps) {
      resetCopilotState();
    }
  };

  const restartCopilotSession = useCallback(async () => {
    if (isOldImplementation) {
      if (threadId) {
        endCopilotSession();
      } else {
        resetCopilotState();
      }
      await queryClient.refetchQueries({ queryKey: [QueryKey.Copilot, QueryKey.InitChatbotSession] });
    } else {
      if (sessionId) {
        endCopilotSession();
      }
      resetCopilotState();
    }
  }, [isOldImplementation, sessionId, threadId, endCopilotSession, resetCopilotState, queryClient]);

  const addMessage = useCallback((newMessage: ChatMessage) => setMessages(prevMessages => [...prevMessages, newMessage]), []);

  const agenticConversation = useAgenticConversation({
    isCopilotOpen: isOpen && !isOldImplementation,
    sessionId,
    addMessage,
    setErrorMessage: setConversationErrorMessage,
    setSessionId,
    lastSessionProps,
    excludeStateUpdate,
    stateRef,
  });

  const chatbotConversation = useChatbotConversation({
    isCopilotOpen: isOpen && isOldImplementation,
    chatRunInfo: { threadId },
    addMessage,
    setErrorMessage: setConversationErrorMessage,
  });

  const forceDiscardCopilot = useCallback(
    (keepSession = lastSessionProps.retainSessionOnClose) => {
      setIsOpen(false);
      if (keepSession) {
        return;
      }
      if (lastSessionProps.retainSessionOnClose) {
        setLastSessionProps(prev => ({ ...prev, retainSessionOnClose: false }));
      }
      resetCopilotState();
      queryClient.removeQueries({ queryKey: [QueryKey.Copilot] });
      if (isOldImplementation) {
        if (threadId) {
          endOldSession({ chatRunInfo: { threadId } });
        }
      } else if (sessionId) {
        endSession({ sessionId });
      }
    },
    [endSession, endOldSession, resetCopilotState, sessionId, threadId, isOldImplementation, lastSessionProps.retainSessionOnClose]
  );

  const initBasePathToRetain = useOnSubNavigate(forceDiscardCopilot, lastSessionProps.retainSessionOnClose);

  const closeCopilotWithBlocker = useCallback(() => {
    if (isDirty) {
      setIsNavigationBlockerOpen(true);
    } else {
      forceDiscardCopilot();
    }
  }, [isDirty, forceDiscardCopilot]);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      threadId,
      setThreadId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      postMessage: agenticConversation.postMessage,
      publishPrompt: chatbotConversation.publishMessage,
      summarizeMessage: chatbotConversation.summarizeMessage,
      isPollingEnabled: isOldImplementation ? chatbotConversation.isPollingEnabled : agenticConversation.isPollingEnabled,
      thinkingText: agenticConversation.thinkingText ?? 'Thinking',
      isSummarizing: chatbotConversation.isSummarizing,
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      lastSessionProps,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
      isDefaultPromptsOpen,
      setIsDefaultPromptsOpen,
      showActionButtons,
      setShowActionButtons,
      excludeStateUpdate,
      setExcludeStateUpdate,
      stateRef,
      isSSEEnabled,
    }),
    [
      isOpen,
      setIsOpen,
      sessionId,
      setSessionId,
      threadId,
      setThreadId,
      restartCopilotSession,
      endCopilotSession,
      forceDiscardCopilot,
      closeCopilotWithBlocker,
      messages,
      addMessage,
      agenticConversation.postMessage,
      chatbotConversation.publishMessage,
      chatbotConversation.summarizeMessage,
      isOldImplementation,
      chatbotConversation.isPollingEnabled,
      agenticConversation.isPollingEnabled,
      agenticConversation.thinkingText,
      chatbotConversation.isSummarizing,
      conversationErrorMessage,
      setConversationErrorMessage,
      onOpenCopilot,
      lastSessionProps,
      isDirty,
      isNavigationBlockerOpen,
      setIsNavigationBlockerOpen,
      isDefaultPromptsOpen,
      setIsDefaultPromptsOpen,
      showActionButtons,
      setShowActionButtons,
      excludeStateUpdate,
      setExcludeStateUpdate,
      isSSEEnabled,
    ]
  );

  return <AvCopilotContext.Provider value={value}>{children}</AvCopilotContext.Provider>;
}

export const useAvCopilotContext = () => useContext(AvCopilotContext);
